import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';

import { format } from 'sql-formatter';
import { OptionGroup, RuleGroupType, formatQuery } from 'react-querybuilder';

import { DataSourceAlarmMetadata } from 'types/types';
import { Editor } from '@monaco-editor/react';

export interface Ref {
  handleUpdateSQLQuery: (query: RuleGroupType) => void;
}

interface Props {
  queryOperators: OptionGroup[];
  dataSourceMetadataForAlarm?: DataSourceAlarmMetadata;
}

const SQLQueryContainer: ForwardRefRenderFunction<Ref, Props> = (
  { queryOperators, dataSourceMetadataForAlarm },
  ref
) => {
  const [sqlQuery, setSQLQuery] = useState<string>('');

  const queryOperatorsRef = useRef<OptionGroup[]>(queryOperators);
  const dataSourceMetadataForAlarmRef = useRef<
    DataSourceAlarmMetadata | undefined
  >(dataSourceMetadataForAlarm);

  useEffect(() => {
    queryOperatorsRef.current = queryOperators;
    dataSourceMetadataForAlarmRef.current = dataSourceMetadataForAlarm;
  }, [queryOperators, dataSourceMetadataForAlarm]);

  const transformValueFields = (query: any): any => {
    const clonedQuery = { ...query };

    const processRule = (rule: any): any => {
      const clonedRule = { ...rule };

      if (
        clonedRule.value &&
        typeof clonedRule.value === 'object' &&
        'value' in clonedRule.value
      ) {
        clonedRule.value = clonedRule.value.value;
      }

      Object.keys(dataSourceMetadataForAlarmRef.current ?? {}).map((el) => {
        const array = dataSourceMetadataForAlarmRef.current![el];
        if (Array.isArray(array)) {
          const foundEl = array?.find((el) => el.id === clonedRule.field);
          if (foundEl) {
            clonedRule.field = foundEl.name;
            return;
          }
        }
      });

      const operatorFound = queryOperatorsRef.current
        .flatMap((el) => el.options)
        .find((el) => el.name === clonedRule.operator);

      if (operatorFound) {
        clonedRule.operator = operatorFound.label;
      }

      if (clonedRule.rules && clonedRule.rules.length > 0) {
        clonedRule.rules = clonedRule.rules.map(processRule);
      }

      return clonedRule;
    };

    clonedQuery.rules = clonedQuery.rules.map(processRule);

    return clonedQuery;
  };

  const handleUpdateSQLQuery = (query: RuleGroupType) => {
    try {
      const formattedQuery = formatQuery(transformValueFields(query), 'sql');
      setSQLQuery(format(formattedQuery, { language: 'mysql', tabWidth: 4 }));
    } catch {
      setSQLQuery('Something went wrong!');
    }
  };

  useImperativeHandle(ref, () => ({
    handleUpdateSQLQuery,
  }));

  return (
    <div>
      <div className="editor-container">
        <Editor
          value={sqlQuery}
          height="10rem"
          defaultLanguage="mysql"
          theme="vs-light"
          options={{
            fontSize: 14,
            lineNumbers: 'off',
            minimap: { enabled: false },
            readOnly: true,
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            tabSize: 4,
            padding: {
              top: 10,
              bottom: 10,
            },
            renderLineHighlight: 'none',
            overviewRulerLanes: 0,
          }}
        />
      </div>
    </div>
  );
};

export default forwardRef<Ref, Props>(SQLQueryContainer);
